import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { CollapsibleSettingsSection } from "./CollapsibleSettingsSection";
import { DynamicsSyncSection } from "./DynamicsSyncSection";
import { HubspotDealCreationSection } from "./HubspotDealCreationSection";
import { HubspotLeadSyncSection } from "./HubspotLeadSyncSection";
import { HubspotSyncSection } from "./HubspotSyncSection";
import { PipedriveSyncSection } from "./PipedriveSyncSection";
import { SalesforceSyncSection } from "./SalesforceSyncSection";
import { ZohoCampaignLinkSection } from "./ZohoCampaignLinkSection";
import { ZohoSyncSection } from "./ZohoSyncSection";

type HubspotSyncProps = Parameters<typeof HubspotSyncSection>[0];
type HubspotLeadProps = Parameters<typeof HubspotLeadSyncSection>[0];
type HubspotDealProps = Parameters<typeof HubspotDealCreationSection>[0];
type ZohoProps = Parameters<typeof ZohoSyncSection>[0];
type PipedriveProps = Parameters<typeof PipedriveSyncSection>[0];
type DynamicsProps = Parameters<typeof DynamicsSyncSection>[0];
type SalesforceProps = Parameters<typeof SalesforceSyncSection>[0];

/**
 * One provider's slice of the props, so adding a CRM adds one field rather than five.
 *
 * The trigger types are read off the section components rather than named here: they are the
 * `…WithInherit` variants, and restating them by hand is how a prop silently drifts from the
 * component it feeds.
 */
interface ProviderSection<TProps extends { listSyncTrigger: unknown }, TTrigger> {
	/** `useIsFeatureActive` answers `undefined` while the flags load; falsy is the right read. */
	isEnabled: boolean | undefined;
	isConnected: boolean | undefined;
	listSyncTrigger: TProps["listSyncTrigger"];
	selected: TTrigger;
	setSelected: (trigger: NonNullable<TTrigger>) => void;
}

interface ListEditSettingCrmSectionsProps {
	listId: number;
	hubspot: {
		isEnabled: boolean | undefined;
		isConnected: boolean | undefined;
		listSyncTrigger: HubspotSyncProps["listSyncTrigger"];
		selectedSyncTrigger: HubspotSyncProps["selectedHubspotSyncTrigger"];
		setSelectedSyncTrigger: HubspotSyncProps["setSelectedHubspotSyncTrigger"];
		listLeadSyncTrigger: HubspotLeadProps["listLeadSyncTrigger"];
		selectedLeadSyncTrigger: HubspotLeadProps["selectedLeadSyncTrigger"];
		setSelectedLeadSyncTrigger: HubspotLeadProps["setSelectedLeadSyncTrigger"];
		dealCreationEffectiveTrigger: HubspotDealProps["effectiveTrigger"];
		selectedDealCreationTrigger: HubspotDealProps["selectedDealCreationTrigger"];
		setSelectedDealCreationTrigger: HubspotDealProps["setSelectedDealCreationTrigger"];
	};
	zoho: ProviderSection<ZohoProps, ZohoProps["selectedZohoSyncTrigger"]>;
	pipedrive: ProviderSection<PipedriveProps, PipedriveProps["selectedPipedriveSyncTrigger"]>;
	dynamics: ProviderSection<DynamicsProps, DynamicsProps["selectedDynamicsSyncTrigger"]>;
	salesforce: ProviderSection<SalesforceProps, SalesforceProps["selectedSalesforceSyncTrigger"]>;
}

/**
 * The per-list CRM settings blocks, lifted out of `ListEditSetting.tsx`.
 *
 * That file is on the shrink-only file-length ratchet, so a fifth CRM could not be added to it
 * without a reduction in the same change — and the block group was the obvious candidate: five
 * near-identical collapsible cards that have nothing to do with the rest of the page.
 *
 * The HubSpot and Zoho cards previously hand-rolled the collapsible markup that
 * {@link CollapsibleSettingsSection} already provides, verbatim, including the two `useState`
 * flags that lived on the page for no other reason. Both now use the shared card, which is a
 * pure refactor — same classes, same chevron, same open/closed backgrounds — and takes the
 * per-page state with it. **Provider #6 now costs `ListEditSetting.tsx` zero lines.**
 */
export const ListEditSettingCrmSections: FunctionComponent<ListEditSettingCrmSectionsProps> = ({
	listId,
	hubspot,
	zoho,
	pipedrive,
	dynamics,
	salesforce,
}) => {
	const { t } = useTranslation();
	const tsSettings = (key: string) => t(`list.settings.${key}`);

	return (
		<>
			{hubspot.isConnected && hubspot.isEnabled && (
				<CollapsibleSettingsSection
					description={tsSettings("hubspotSettingsDescription")}
					title={tsSettings("hubspotSettings")}
				>
					<HubspotSyncSection
						isConnected={Boolean(hubspot.isConnected)}
						isHubspotEnabled={Boolean(hubspot.isEnabled)}
						listSyncTrigger={hubspot.listSyncTrigger}
						selectedHubspotSyncTrigger={hubspot.selectedSyncTrigger}
						setSelectedHubspotSyncTrigger={hubspot.setSelectedSyncTrigger}
					/>
					<HubspotLeadSyncSection
						isConnected={Boolean(hubspot.isConnected)}
						isHubspotEnabled={Boolean(hubspot.isEnabled)}
						listLeadSyncTrigger={hubspot.listLeadSyncTrigger}
						selectedLeadSyncTrigger={hubspot.selectedLeadSyncTrigger}
						setSelectedLeadSyncTrigger={hubspot.setSelectedLeadSyncTrigger}
					/>
					<HubspotDealCreationSection
						effectiveTrigger={hubspot.dealCreationEffectiveTrigger}
						isConnected={Boolean(hubspot.isConnected)}
						isHubspotEnabled={Boolean(hubspot.isEnabled)}
						selectedDealCreationTrigger={hubspot.selectedDealCreationTrigger}
						setSelectedDealCreationTrigger={hubspot.setSelectedDealCreationTrigger}
					/>
				</CollapsibleSettingsSection>
			)}

			{zoho.isConnected && zoho.isEnabled && (
				<CollapsibleSettingsSection
					description={tsSettings("zohoSettingsDescription")}
					title={tsSettings("zohoSettings")}
				>
					<ZohoSyncSection
						isConnected={Boolean(zoho.isConnected)}
						isZohoEnabled={Boolean(zoho.isEnabled)}
						listSyncTrigger={zoho.listSyncTrigger}
						selectedZohoSyncTrigger={zoho.selected}
						setSelectedZohoSyncTrigger={zoho.setSelected}
					/>
					<ZohoCampaignLinkSection
						isConnected={Boolean(zoho.isConnected)}
						isZohoEnabled={Boolean(zoho.isEnabled)}
						listId={listId}
					/>
				</CollapsibleSettingsSection>
			)}

			{pipedrive.isConnected && pipedrive.isEnabled && (
				<CollapsibleSettingsSection
					description={tsSettings("pipedriveSettingsDescription")}
					title={tsSettings("pipedriveSettings")}
				>
					<PipedriveSyncSection
						isConnected={Boolean(pipedrive.isConnected)}
						isPipedriveEnabled={Boolean(pipedrive.isEnabled)}
						listSyncTrigger={pipedrive.listSyncTrigger}
						selectedPipedriveSyncTrigger={pipedrive.selected}
						setSelectedPipedriveSyncTrigger={pipedrive.setSelected}
					/>
				</CollapsibleSettingsSection>
			)}

			{dynamics.isConnected && dynamics.isEnabled && (
				<CollapsibleSettingsSection
					description={tsSettings("msdynamicsSettingsDescription")}
					title={tsSettings("msdynamicsSettings")}
				>
					<DynamicsSyncSection
						isConnected={Boolean(dynamics.isConnected)}
						isDynamicsEnabled={Boolean(dynamics.isEnabled)}
						listSyncTrigger={dynamics.listSyncTrigger}
						selectedDynamicsSyncTrigger={dynamics.selected}
						setSelectedDynamicsSyncTrigger={dynamics.setSelected}
					/>
				</CollapsibleSettingsSection>
			)}

			{salesforce.isConnected && salesforce.isEnabled && (
				<CollapsibleSettingsSection
					description={tsSettings("salesforceSettingsDescription")}
					title={tsSettings("salesforceSettings")}
				>
					<SalesforceSyncSection
						isConnected={Boolean(salesforce.isConnected)}
						isSalesforceEnabled={Boolean(salesforce.isEnabled)}
						listSyncTrigger={salesforce.listSyncTrigger}
						selectedSalesforceSyncTrigger={salesforce.selected}
						setSelectedSalesforceSyncTrigger={salesforce.setSelected}
					/>
				</CollapsibleSettingsSection>
			)}
		</>
	);
};
